import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useFarmSurvivalSampleSize } from '@/components/pond/page/survivalChecks';
import { api } from '@/lib/api';
import { saveFailureMessage } from '@/lib/apiErrors';
import { parseTypedAmount } from '@/lib/number';

const MAX_INT = 2147483647;

export function SurvivalSampleSizeEditor({ farmId, canEdit }: { farmId: string; canEdit: boolean }) {
  const saved = useFarmSurvivalSampleSize(farmId);
  return <Editor key={saved ?? 'unset'} farmId={farmId} saved={saved} canEdit={canEdit} />;
}

function Editor({ farmId, saved, canEdit }: { farmId: string; saved: number | null; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState(saved === null ? '' : String(saved));
  const parsed = parseTypedAmount(text, { integer: true });
  const value = 'value' in parsed ? parsed.value : null;
  const problem =
    text.trim() === '' ? null : 'error' in parsed ? parsed.error : value === null || value <= 0 ? 'More than 0.' : value > MAX_INT ? 'Too large.' : null;
  const dirty = value !== null && !problem && value !== saved;

  const save = useMutation({
    mutationFn: () => api.put(`/api/v1/farms/${farmId}/settings`, { survival_sample_size: value }),
    onSuccess: () => {
      toast.success('Survival sample size saved');
      queryClient.invalidateQueries({ queryKey: ['farm', farmId] });
    },
  });

  return (
    <Card>
      <Card.Header>
        <Card.Title>Survival check sample</Card.Title>
      </Card.Header>
      <Card.Body>
        <p className="mb-3 text-sm text-gray-500">
          How many shrimp the farm counts in an early survival check. A new check starts with this number; it can still be
          changed on each check. Left empty, the form asks every time.
          {!canEdit && ' Only a Farm Owner can change this.'}
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1.5 text-sm font-medium text-gray-900">
            Sample size
            <input
              data-survival-sample-size
              inputMode="numeric"
              autoComplete="off"
              value={text}
              disabled={!canEdit}
              aria-invalid={!!problem || undefined}
              onChange={(e) => setText(e.target.value)}
              className="h-11 w-40 rounded-lg border border-gray-300 px-3 text-base tabular-nums disabled:bg-gray-50"
            />
          </label>
          {canEdit && (
            <Button type="button" className="h-11" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          )}
        </div>
        {problem && <p className="mt-2 text-sm text-red-700">{problem}</p>}
        {save.isError && <p className="mt-2 text-sm text-red-700">Not saved: {saveFailureMessage(save.error, 'try again')}</p>}
      </Card.Body>
    </Card>
  );
}
